/**
 * Reads an untrusted bundle file into a sanitized `SettingsBundle`.
 *
 * The failure policy, chosen so a bad file can never half-apply:
 *   - wrong FILE (bad JSON, wrong kind, future version, `groups` not an
 *     object) → hard error, the caller applies nothing;
 *   - deny-listed content anywhere → hard error, because a settings backup
 *     carrying a credential is not one we wrote;
 *   - a broken toolbar layout → hard error, per B11's instruction that
 *     "same id on both surfaces" must reach the user rather than be
 *     silently tie-broken by `mergeToolbarLayout`;
 *   - everything else unknown or mistyped → dropped with a warning, so a
 *     bundle from a newer build still imports what this build understands.
 *
 * The output only ever contains keys the manifest declares — the parser
 * rebuilds the bundle field by field rather than filtering the input, so an
 * unexpected key cannot survive by being overlooked.
 */
import { groupById, isDeniedStoreKey, type SettingsBundleGroupId } from './settingsBundleManifest';
import { assertNoDeniedData } from './settingsBundleGuard';
import { isRecord, sanitizeSlice } from './sanitizeBundleSlice';
import { isSafePathSegment } from '../../../types/safePathSegment';
import {
  SETTINGS_BUNDLE_KIND,
  SETTINGS_BUNDLE_VERSION,
  type BundleWarning,
  type GroupPayload,
  type ParsedBundle,
  type SettingsBundle,
} from './settingsBundleTypes';

const NOT_A_BUNDLE =
  "This file isn't a Jam SQL Studio settings backup. Pick a file exported from Settings → Backup & Transfer.";

export function parseSettingsBundle(text: string): ParsedBundle {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, error: "This isn't a valid JSON file." };
  }
  if (!isRecord(raw) || raw.kind !== SETTINGS_BUNDLE_KIND) {
    return { ok: false, error: NOT_A_BUNDLE };
  }
  if (typeof raw.version !== 'number' || !Number.isInteger(raw.version) || raw.version < 1) {
    return { ok: false, error: NOT_A_BUNDLE };
  }
  if (raw.version > SETTINGS_BUNDLE_VERSION) {
    return {
      ok: false,
      error:
        'This backup was made by a newer version of Jam SQL Studio. Update the app and try again.',
    };
  }
  if (raw.groups !== undefined && !isRecord(raw.groups)) {
    return { ok: false, error: 'This backup\'s `groups` section is not readable.' };
  }

  const warnings: BundleWarning[] = [];
  const groups: SettingsBundle['groups'] = {};

  try {
    for (const [groupId, payload] of Object.entries((raw.groups ?? {}) as Record<string, unknown>)) {
      const group = groupById(groupId);
      if (!group) {
        warnings.push({ code: 'unknown-group', message: `"${groupId}" isn't a settings group this version knows — it was skipped.` });
        continue;
      }
      if (!isRecord(payload)) {
        warnings.push({ code: 'wrong-type', message: `The "${group.label}" section is not readable — it was skipped.` });
        continue;
      }
      const slices: GroupPayload = {};
      for (const [storeKey, storePayload] of Object.entries(payload)) {
        if (isDeniedStoreKey(storeKey)) {
          return {
            ok: false,
            error:
              `This backup contains "${storeKey}", which never travels in a settings ` +
              'file. Nothing was imported.',
          };
        }
        const slice = group.slices.find((s) => s.store === storeKey);
        if (!slice) {
          warnings.push({ code: 'unknown-store', message: `"${storeKey}" isn't part of "${group.label}" in this version — it was skipped.` });
          continue;
        }
        if (!isRecord(storePayload)) {
          warnings.push({ code: 'wrong-type', message: `"${storeKey}" is not readable — it was skipped.` });
          continue;
        }
        const sanitized = sanitizeSlice(storePayload, slice.fields, storeKey, warnings);
        if (Object.keys(sanitized).length > 0) slices[storeKey] = sanitized;
      }
      if (Object.keys(slices).length > 0) groups[groupId as SettingsBundleGroupId] = slices;
    }
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'This backup could not be read.' };
  }

  const bundle: SettingsBundle = {
    kind: SETTINGS_BUNDLE_KIND,
    version: SETTINGS_BUNDLE_VERSION,
    exportedAt: typeof raw.exportedAt === 'string' ? raw.exportedAt : '',
    ...(typeof raw.appVersion === 'string' ? { appVersion: raw.appVersion } : {}),
    groups,
  };

  const metaInfo = sanitizeMetaInfo(raw.metaInfo, warnings);
  if (metaInfo) bundle.metaInfo = metaInfo;

  try {
    assertNoDeniedData(bundle);
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'This backup could not be read.' };
  }
  return { ok: true, bundle, warnings };
}

function sanitizeMetaInfo(
  value: unknown,
  warnings: BundleWarning[],
): Array<Record<string, unknown>> | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) {
    warnings.push({ code: 'metainfo.bad-shape', message: 'The meta-info section is not readable — it was skipped.' });
    return undefined;
  }
  // `connectionId` and `database` are not just identifiers — they become
  // filesystem path segments in `metaInfoStore.filePath`, and `path.join`
  // collapses `..`. A type check alone let an imported file choose the write
  // target (`connectionId: '..'`, `database: 'connections'` overwrote
  // `{userData}/connections.json`). They must be safe single segments.
  const kept = value.filter(
    (r) =>
      isRecord(r) &&
      isSafePathSegment(r.connectionId) &&
      isSafePathSegment(r.database) &&
      Array.isArray(r.looseRelationships),
  ) as Array<Record<string, unknown>>;
  if (kept.length !== value.length) {
    warnings.push({
      code: 'metainfo.bad-record',
      message: `${value.length - kept.length} meta-info record(s) were not readable and were skipped.`,
    });
  }
  return kept;
}
